import LZString from 'lz-string';
import { useEffect } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';

import type { RevelioPageId } from '../../components/PageConfigProvider/types';
import { isScreenerV2Page } from '../../utils/routing-helpers';
import type { ConsistencyKey } from '../engine/filterGroupConsistency';
import { type Filter, FilterId } from '../types/filters';

export const useSyncFiltersToUrl = (
  filtersState: [ConsistencyKey, Filter][],
  roleTaxonomyVersion?: string,
  skillTaxonomyVersion?: string,
  revelioPageId?: RevelioPageId,
  enabled = true
) => {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    if (!enabled) {
      return;
    }

    if (location.pathname.startsWith('/report-builder')) {
      return;
    }

    // Bail when a navigation AWAY from this page is in flight. This provider
    // can still be mounted (and re-fire this effect from a late filter settle,
    // e.g. correction-preference sync landing under load) after the browser URL
    // has already moved to another route. The `setSearchParams` setter is bound
    // to THIS page's react-router location, so writing now would `replace` the
    // new route's URL back to this page — silently reverting an external
    // navigation (e.g. a global-AI-assistant `navigate`). Comparing the live
    // browser pathname against the router pathname this effect captured detects
    // that mismatch; in steady state they are identical, so this is a no-op.
    if (
      typeof window !== 'undefined' &&
      window.location.pathname !== location.pathname
    ) {
      return;
    }

    const currentSerializedFilters = searchParams.get('filters');
    const currentSerializedFilterGroups = searchParams.get('filterGroups');
    if (currentSerializedFilterGroups) return;

    const isScreenerPage = isScreenerV2Page(revelioPageId);

    // For screener pages: exclude taxonomy filters (matching old screener behavior)
    // For other pages: include taxonomy filters with version info
    const filtersToSerialize = isScreenerPage
      ? filtersState.filter(
          ([id]: [ConsistencyKey, Filter]) =>
            id !== FilterId.ROLE_TAXONOMY && id !== FilterId.SKILL_TAXONOMY
        )
      : filtersState.map(([id, filter]: [ConsistencyKey, Filter]) => {
          if (
            (id === FilterId.ROLE_TAXONOMY && roleTaxonomyVersion) ||
            (id === FilterId.SKILL_TAXONOMY && skillTaxonomyVersion)
          ) {
            const taxonomyFilter = filter;

            if ('value' in taxonomyFilter && taxonomyFilter.value) {
              return [
                id,
                {
                  ...taxonomyFilter,
                  value: {
                    ...taxonomyFilter.value,
                    version:
                      id === FilterId.ROLE_TAXONOMY
                        ? roleTaxonomyVersion
                        : skillTaxonomyVersion,
                  },
                },
              ];
            }
          }

          return [id, filter];
        });

    const newSerializedFilters = LZString.compressToEncodedURIComponent(
      JSON.stringify(filtersToSerialize)
    );

    if (currentSerializedFilters !== newSerializedFilters) {
      setSearchParams(
        (prev) => {
          const nextParams = new URLSearchParams(prev);
          nextParams.set('filters', newSerializedFilters);
          // Reset pagination when filters change to keep results consistent.
          nextParams.delete('page');
          return nextParams;
        },
        { replace: true }
      );
    }
  }, [
    enabled,
    filtersState,
    searchParams,
    setSearchParams,
    roleTaxonomyVersion,
    skillTaxonomyVersion,
    revelioPageId,
    location.pathname,
  ]);
};
